import { PendingVoiceConversionState } from 'apps/chat/actions/interfaces'
import { LiveChatThread } from 'apps/chat/constants'
import {
  ConversationFilterType,
  StatusFilterType,
  ThreadsSortingTypes,
  Sender,
  MsginEvent,
  AssignEvent,
  UserThreadNewEvent,
  SelectionType,
} from 'apps/chat/constants/LiveChatInterfaces'
import { LabelsState } from 'apps/chat/interfaces/labels'
import { ChatMessageInterface } from 'apps/chat/types/chatMessageTypes'
import { ChannelType } from 'common/core/constants/ChannelType'
import { Filter, FilterCondition } from 'common/filter/interfaces'
import { IAdminInterface } from 'common/settings/interfaces'
import { PreloadImagesArray } from 'components/RichTextEditor/processor/constants'
import { AiConversationRate } from 'shared/api/requests/aiConversationFeedback/schemas'
import { HopError, ThreadControlError } from 'shared/api/requests/chat/guards'
import { ScheduledMessage } from 'shared/api/requests/scheduledMessages/schemas'
import { ILimitedList, ILimitedListCurrent } from 'utils/factory/limitedList/interfaces'

export interface ChatState {
  admins: ILimitedList<IAdminInterface>
  chatSettings: ChatSettingsState
  counters: CountersState
  labels: LabelsState
  messages: MessagesState
  notifications: ChatNotifications
  search: SearchState
  threads: ThreadsState
  state: ChatStateField
  scheduledMessages: ScheduledMessage[] | null
  aiConversationsRate: AiConversationsRateState
}

export interface MessagesState extends ILimitedList<ChatMessageInterface> {
  hideAutomation: boolean
  scrollDisabled?: boolean
  listState: {
    isLoading: boolean
    isLoaded: boolean
    isUpdating: boolean
  }
  listRequestsInfo: {
    lastRequestId: string
    lastRequestPayloadHash: string
  }
  fetchArgs?: string
  hasDropItems: boolean
}

export interface SearchState extends ILimitedList<LiveChatThread> {
  activeMessageId: number | null
}

export interface CountersState {
  isFetching: boolean
  isThreadsAssignmentChanged: boolean
  users: UserCounters[] | null
  unreadThreads: UnreadThreads | null
}

export interface AiConversationsRateState {
  conversations: Record<string, AiConversationRate>
}

export type UploadedImagesThreadsState = Record<
  string,
  {
    [key in ChannelType]?: PreloadImagesArray
  }
>

export type LoadingMessagesThreads = Record<
  string,
  {
    [key in ChannelType]?: string[]
  }
>

export interface ThreadsState extends ILimitedList<LiveChatThread> {
  assignee: number | null
  channel: ChannelType
  checkedFilterIds: Record<string, { loading: boolean; satisfy?: boolean }>
  current: CurrentThread
  currentId: string | null
  fetchingPauseIds: Record<string, boolean>
  filter: Filter<FilterCondition>
  isHistoryModal: boolean
  selectedThreads: Record<string, LiveChatThread>
  selectionType: SelectionType
  statusFilter: StatusFilterType
  threadsSorting: ThreadsSortingTypes
  totalThreadsCount: number | null
  uploadedImagesThreads: UploadedImagesThreadsState
  loadingMessagesThreads: LoadingMessagesThreads
  conversationsFilter: ConversationFilterType
  channelsFilter: ChannelType | null
  teamMembersFilter: number
  pendingVoiceConversionStates: PendingVoiceConversionState[]
}

export interface CurrentThread extends ILimitedListCurrent<LiveChatThread> {
  freeEntryPointWindow: ConversationWindowStatus
  customerServiceWindow: ConversationWindowStatus
  threadControlError?:
    | ThreadControlError['error_data']['thread_control_error']
    | HopError['error_data']['hop_error']
    | null
}

export interface ChatSettingsState {
  isAllChannelsHistoryModalOpen: boolean
}

export interface ThreadItem {
  user_id: string
}

export interface MessageItem {
  id: number
  model: UnsafeAnyObject
  page_id: number
  sort_id: number
  timestamp: number
  type: string
  user_id: number
  reactions?: string[]
}

export interface InstagramMessageModel {
  type: string
  mid: string
}

export interface LiveChatMessageModel {
  messages: InstagramOutMessage[]
  sender: Sender
  _oid: string
}

export interface InstagramMessageOptions {
  id: number
  type: string
  model: InstagramMessageModel | LiveChatMessageModel
}

export interface InstagramOutMessage {
  content: {
    text: string
  }
  keyboard: UnsafeAnyObject[] | null
  reactions?: string[]
  _oid: string
  _mid: string
}

export interface UserCounters {
  account_id: AccountId
  user_id: number
  thread_counters: {
    account: AccountThreadsCounters
    user: UserThreadsCounters
  }
}

export interface UnreadThreads {
  unassigned: boolean
  on_me: boolean
  account_id: AccountId
}

export interface AccountThreadsCounters {
  all: number
  all_open: number
  assigned_open: number
  unassigned_open: number
}

export interface ChatNotifications {
  messageReceived: LiveChatThread<MsginEvent> | null
  threadAssignedToMe: AssignEvent | null
  threadUnassigned: UserThreadNewEvent | null
}

export interface UserThreadsCounters {
  assigned_open: number
  assigned_closed: number
  assigned: number
  mentioned_unread: number
}

export interface ChatStateField {
  isSnippetsListOpened: boolean
  isEnableNotificationsModalOpened: boolean
  isScheduleMessageModalOpened: boolean
  wasMessageScheduled: boolean
  scheduledMessageText: string | null
}

export enum ConversationWindowType {
  FREE_ENTRY_POINT = 'freeEntryPointWindow',
  CUSTOMER_SERVICE = 'customerServiceWindow',
}

export interface ConversationWindowData {
  openUntil: number
  expirationDate: Date | null
  expirationTime: string | null
  isOpen: boolean
  isExpired: boolean
}

export type ConversationWindowStatus = Pick<ConversationWindowData, 'isOpen' | 'isExpired'>
export type ConversationWindowState = Record<ConversationWindowType, ConversationWindowStatus>
